// SettingsView - Full content view for app settings
// Consolidates General, Knowledge, Patterns, Outreach, and API settings

import { API_BASE_URL } from '../config/settings';

// Helper for knowledge settings API calls (GDrive, Uniscribe, YouTube)
const knowledgeSettings = {
  // GDrive
  getGDriveStatus: async () => {
    const res = await fetch(`${API_BASE_URL}/knowledge/gdrive/status`);
    return res.json();
  },
  getGDriveConfig: async () => {
    const res = await fetch(`${API_BASE_URL}/knowledge/gdrive/config`);
    return res.json();
  },
  getGDriveFolders: async () => {
    const res = await fetch(`${API_BASE_URL}/knowledge/gdrive/folders`);
    return res.json();
  },
  saveGDriveConfig: async (config: { folders: Array<{ id: string; name: string }>; autoSync: boolean }) => {
    const res = await fetch(`${API_BASE_URL}/knowledge/gdrive/config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config)
    });
    return res.json();
  },
  syncGDrive: async () => {
    const res = await fetch(`${API_BASE_URL}/knowledge/gdrive/sync`, { method: 'POST' });
    return res.json();
  },

  // Uniscribe
  getUniscribeStatus: async () => {
    const res = await fetch(`${API_BASE_URL}/knowledge/uniscribe/status`);
    return res.json();
  },
  getUniscribeConfig: async () => {
    const res = await fetch(`${API_BASE_URL}/knowledge/uniscribe/config`);
    return res.json();
  },
  saveUniscribeConfig: async (config: { apiKey: string; autoSync: boolean }) => {
    const res = await fetch(`${API_BASE_URL}/knowledge/uniscribe/config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config)
    });
    return res.json();
  },
  deleteUniscribeConfig: async () => {
    const res = await fetch(`${API_BASE_URL}/knowledge/uniscribe/config`, { method: 'DELETE' });
    return res.json();
  },
  syncUniscribe: async () => {
    const res = await fetch(`${API_BASE_URL}/knowledge/uniscribe/sync`, { method: 'POST' });
    return res.json();
  },

  // YouTube
  getYouTubeStatus: async () => {
    const res = await fetch(`${API_BASE_URL}/knowledge/youtube/status`);
    return res.json();
  },
  addYouTubePlaylist: async (playlistUrl: string) => {
    const res = await fetch(`${API_BASE_URL}/knowledge/youtube/playlists`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playlistUrl })
    });
    return res.json();
  },
  removeYouTubePlaylist: async (playlistId: string) => {
    const res = await fetch(`${API_BASE_URL}/knowledge/youtube/playlists/${playlistId}`, { method: 'DELETE' });
    return res.json();
  },
  syncYouTube: async () => {
    const res = await fetch(`${API_BASE_URL}/knowledge/youtube/sync`, { method: 'POST' });
    return res.json();
  },
  processYouTubePending: async () => {
    const res = await fetch(`${API_BASE_URL}/knowledge/youtube/process`, { method: 'POST' });
    return res.json();
  }
};

type SettingsSection = 'general' | 'knowledge' | 'patterns' | 'outreach' | 'api';

interface AppSettings {
  fields: Array<{
    id: string;
    label: string;
    type: string;
    layout: string;
    options?: string[];
    defaultVisible?: boolean;
  }>;
  areas: string[];
  resourceCategories: string[];
  platforms?: string[];
  contentFormats?: string[];
}

interface IngestDocs {
  endpoint: string;
  method: string;
  headers: Record<string, string>;
  body: Record<string, unknown>;
  examples: Record<string, unknown>;
  urlTypeDetection: Record<string, string>;
  debugEndpoint?: { url: string };
}

interface IngestLogEntry {
  timestamp: number;
  headers: Record<string, string>;
  body: unknown;
  result: { success: boolean; error?: string; id?: string };
}

interface GDriveFolder {
  id: string;
  name: string;
  path?: string;
}

export class SettingsView {
  private containerEl: HTMLElement | null = null;
  private activeSection: SettingsSection = 'general';

  // General settings
  private settings: AppSettings | null = null;

  // Knowledge settings
  private availableFolders: GDriveFolder[] = [];
  private selectedFolders: GDriveFolder[] = [];
  private gdriveAutoSync: boolean = true;
  private gdriveLastSync: number | null = null;
  private gdriveFilesIndexed: number = 0;
  private gdriveConnected: boolean = false;
  private gdriveSyncing: boolean = false;
  private uniscribeConfigured: boolean = false;
  private uniscribeApiKeyPreview: string | null = null;
  private uniscribeAutoSync: boolean = true;
  private uniscribeLastSync: number | null = null;
  private uniscribeTranscriptionsIndexed: number = 0;
  private uniscribeSyncing: boolean = false;
  private youtubeApiKeyConfigured: boolean = false;
  private youtubePlaylists: { id: string; title: string }[] = [];
  private youtubeAutoSync: boolean = true;
  private youtubeLastCheck: number | null = null;
  private youtubeVideosProcessed: number = 0;
  private youtubeVideosPending: number = 0;
  private youtubePendingVideos: { videoId: string; title: string; sentAt: number }[] = [];
  private youtubeSyncing: boolean = false;
  private youtubeProcessing: boolean = false;

  // API settings
  private ingestDocs: IngestDocs | null = null;
  private ingestLogs: IngestLogEntry[] = [];
  private ingestTotal: number = 0;
  private ingestPrefilled: number = 0;
  private ingestManual: number = 0;
  private ingestLastIngest: number | null = null;
  private isRetraining: boolean = false;

  // Source activity
  private sourceActivity: {
    summary: Record<string, {
      total: number;
      success: number;
      failed: number;
      lastActivity: number;
      byType: Record<string, number>;
    }>;
    last24hBySource: Record<string, number>;
    recentLogs: Array<{
      source: string;
      type: string;
      success: boolean;
      title?: string;
      id?: string;
      timestamp: number;
    }>;
  } | null = null;

  private isLoading: boolean = false;

  constructor() {
    this.containerEl = document.getElementById('settings-view');
  }

  async init(): Promise<void> {
    if (!this.containerEl) return;
    this.render();
    await this.loadAllData();
  }

  private async loadAllData(): Promise<void> {
    this.isLoading = true;
    this.renderContent();

    try {
      await Promise.all([
        this.loadGeneralSettings(),
        this.loadKnowledgeData(),
        this.loadApiData()
      ]);
    } catch (error) {
      console.error('Failed to load settings data:', error);
    }

    this.isLoading = false;
    this.renderContent();
  }

  private async loadGeneralSettings(): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/settings`);
      const data = await response.json();
      if (data.success) {
        this.settings = data.data;
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  }

  private async loadKnowledgeData(): Promise<void> {
    try {
      // GDrive
      const gdriveStatus = await knowledgeSettings.getGDriveStatus();
      if (gdriveStatus.success && gdriveStatus.data) {
        this.gdriveConnected = gdriveStatus.data.connected;
        this.gdriveFilesIndexed = gdriveStatus.data.filesIndexed;
        this.gdriveLastSync = gdriveStatus.data.lastSync;
        this.gdriveAutoSync = gdriveStatus.data.autoSync;
      }

      if (this.gdriveConnected) {
        const config = await knowledgeSettings.getGDriveConfig();
        if (config.success && config.data) {
          this.selectedFolders = config.data.folders;
          this.gdriveAutoSync = config.data.autoSync;
          this.gdriveLastSync = config.data.lastSync;
        }
        const folders = await knowledgeSettings.getGDriveFolders();
        if (folders.success && folders.data) {
          this.availableFolders = folders.data.folders;
        }
      }

      // Uniscribe
      const uniscribeStatus = await knowledgeSettings.getUniscribeStatus();
      if (uniscribeStatus.success && uniscribeStatus.data) {
        this.uniscribeConfigured = uniscribeStatus.data.configured;
        this.uniscribeTranscriptionsIndexed = uniscribeStatus.data.transcriptionsIndexed;
        this.uniscribeLastSync = uniscribeStatus.data.lastSync;
        this.uniscribeAutoSync = uniscribeStatus.data.autoSync;
      }

      if (this.uniscribeConfigured) {
        const config = await knowledgeSettings.getUniscribeConfig();
        if (config.success && config.data) {
          this.uniscribeApiKeyPreview = config.data.apiKeyPreview;
        }
      }

      // YouTube
      const youtubeStatus = await knowledgeSettings.getYouTubeStatus();
      if (youtubeStatus.success && youtubeStatus.data) {
        this.youtubeApiKeyConfigured = youtubeStatus.data.apiKeyConfigured;
        this.youtubePlaylists = youtubeStatus.data.playlists;
        this.youtubeAutoSync = youtubeStatus.data.autoSync;
        this.youtubeLastCheck = youtubeStatus.data.lastCheck;
        this.youtubeVideosProcessed = youtubeStatus.data.videosProcessed;
        this.youtubeVideosPending = youtubeStatus.data.videosPending;
        this.youtubePendingVideos = youtubeStatus.data.pendingVideos;
      }
    } catch (error) {
      console.error('Failed to load knowledge data:', error);
    }
  }

  private async loadApiData(): Promise<void> {
    try {
      const [docsRes, logsRes, statsRes, activityRes] = await Promise.all([
        fetch(`${API_BASE_URL}/settings/ingest-docs`),
        fetch(`${API_BASE_URL}/ingest/logs`),
        fetch(`${API_BASE_URL}/ingest/stats`),
        fetch(`${API_BASE_URL}/settings/source-activity`)
      ]);

      const docsData = await docsRes.json();
      if (docsData.success) this.ingestDocs = docsData.data;

      const logsData = await logsRes.json();
      if (logsData.success) this.ingestLogs = logsData.data.logs || [];

      const statsData = await statsRes.json();
      if (statsData.success) {
        this.ingestTotal = statsData.data.total;
        this.ingestPrefilled = statsData.data.prefilled;
        this.ingestManual = statsData.data.manual;
        this.ingestLastIngest = statsData.data.lastIngest || null;
      }

      const activityData = await activityRes.json();
      if (activityData.success) {
        this.sourceActivity = activityData.data;
      }
    } catch (error) {
      console.error('Failed to load API data:', error);
    }
  }

  private render(): void {
    if (!this.containerEl) return;

    this.containerEl.innerHTML = `
      <!-- FilterBar -->
      <header class="filter-bar">
        <div class="filter-bar-left">
          <h1 class="filter-bar-title">Settings</h1>
        </div>
        <div class="filter-bar-right">
          <button class="filter-bar-action" id="settings-refresh" title="Refresh">
            <span class="material-symbols-outlined">refresh</span>
          </button>
        </div>
      </header>

      <!-- Settings Layout -->
      <div class="settings-view-layout">
        <!-- Navigation Sidebar -->
        <nav class="settings-nav">
          <button class="settings-nav-item active" data-section="general">
            <span class="material-symbols-outlined">category</span>
            <span>General</span>
          </button>
          <button class="settings-nav-item" data-section="knowledge">
            <span class="material-symbols-outlined">school</span>
            <span>Knowledge</span>
          </button>
          <button class="settings-nav-item" data-section="patterns">
            <span class="material-symbols-outlined">auto_awesome</span>
            <span>Patterns</span>
          </button>
          <button class="settings-nav-item" data-section="outreach">
            <span class="material-symbols-outlined">campaign</span>
            <span>Outreach</span>
          </button>
          <button class="settings-nav-item" data-section="api">
            <span class="material-symbols-outlined">api</span>
            <span>API</span>
          </button>
        </nav>

        <!-- Content Area -->
        <div class="settings-content" id="settings-content">
          <!-- Rendered dynamically -->
        </div>
      </div>
    `;

    this.setupNavListeners();
    this.renderContent();
  }

  private setupNavListeners(): void {
    const navItems = this.containerEl?.querySelectorAll('.settings-nav-item');
    navItems?.forEach(item => {
      item.addEventListener('click', async () => {
        const section = item.getAttribute('data-section') as SettingsSection;
        if (section) {
          this.activeSection = section;
          navItems.forEach(i => i.classList.toggle('active', i === item));

          // Reload data fresh when switching to API section
          if (section === 'api') {
            await this.loadApiData();
          }

          this.renderContent();
        }
      });
    });

    this.containerEl?.querySelector('#settings-refresh')?.addEventListener('click', () => {
      this.loadAllData();
    });
  }

  private renderContent(): void {
    const contentEl = this.containerEl?.querySelector('#settings-content');
    if (!contentEl) return;

    if (this.isLoading) {
      contentEl.innerHTML = `
        <div class="settings-loading">
          <span class="material-symbols-outlined spinning">progress_activity</span>
          <span>Loading settings...</span>
        </div>
      `;
      return;
    }

    switch (this.activeSection) {
      case 'general':
        contentEl.innerHTML = this.renderGeneralSection();
        this.attachGeneralListeners();
        break;
      case 'knowledge':
        contentEl.innerHTML = this.renderKnowledgeSection();
        this.attachKnowledgeListeners();
        break;
      case 'patterns':
        contentEl.innerHTML = this.renderPatternsSection();
        this.attachPatternsListeners();
        break;
      case 'outreach':
        contentEl.innerHTML = this.renderOutreachSection();
        this.attachOutreachListeners();
        break;
      case 'api':
        contentEl.innerHTML = this.renderApiSection();
        this.attachApiListeners();
        break;
    }
  }

  // ============================================
  // GENERAL SECTION
  // ============================================

  private renderGeneralSection(): string {
    if (!this.settings) {
      return '<div class="settings-loading">Loading general settings...</div>';
    }

    return `
      <div class="settings-cards">
        <!-- Categories Card -->
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">category</span>
            <h3>Resource Categories</h3>
          </div>
          <p class="settings-card-hint">Categories for organizing resources. Used by AI classification.</p>
          <div class="settings-list" id="categories-list">
            ${this.settings.resourceCategories.map(cat => `
              <div class="settings-list-item">
                <span>${cat}</span>
                <button class="settings-list-delete" data-type="category" data-value="${cat}">
                  <span class="material-symbols-outlined">delete</span>
                </button>
              </div>
            `).join('')}
          </div>
          <div class="settings-add-row">
            <input type="text" id="new-category-input" placeholder="New category...">
            <button id="add-category-btn" class="settings-add-btn">Add</button>
          </div>
        </div>

        <!-- Platforms Card -->
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">devices</span>
            <h3>Platforms</h3>
          </div>
          <p class="settings-card-hint">Source platforms for content.</p>
          <div class="settings-list" id="platforms-list">
            ${(this.settings.platforms || []).map(plat => `
              <div class="settings-list-item">
                <span>${plat}</span>
                <button class="settings-list-delete" data-type="platform" data-value="${plat}">
                  <span class="material-symbols-outlined">delete</span>
                </button>
              </div>
            `).join('')}
          </div>
          <div class="settings-add-row">
            <input type="text" id="new-platform-input" placeholder="New platform...">
            <button id="add-platform-btn" class="settings-add-btn">Add</button>
          </div>
        </div>

        <!-- Content Formats Card -->
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">video_library</span>
            <h3>Content Formats</h3>
          </div>
          <p class="settings-card-hint">Types of content formats.</p>
          <div class="settings-list" id="formats-list">
            ${(this.settings.contentFormats || []).map(fmt => `
              <div class="settings-list-item">
                <span>${fmt}</span>
                <button class="settings-list-delete" data-type="format" data-value="${fmt}">
                  <span class="material-symbols-outlined">delete</span>
                </button>
              </div>
            `).join('')}
          </div>
          <div class="settings-add-row">
            <input type="text" id="new-format-input" placeholder="New format...">
            <button id="add-format-btn" class="settings-add-btn">Add</button>
          </div>
        </div>

        <!-- PARA Areas Card -->
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">bookmarks</span>
            <h3>PARA Areas</h3>
          </div>
          <p class="settings-card-hint">Areas of responsibility (PARA methodology).</p>
          <div class="settings-list" id="areas-list">
            ${this.settings.areas.map(area => `
              <div class="settings-list-item">
                <span>${area}</span>
                <button class="settings-list-delete" data-type="area" data-value="${area}">
                  <span class="material-symbols-outlined">delete</span>
                </button>
              </div>
            `).join('')}
          </div>
          <div class="settings-add-row">
            <input type="text" id="new-area-input" placeholder="New area...">
            <button id="add-area-btn" class="settings-add-btn">Add</button>
          </div>
        </div>
      </div>
    `;
  }

  private attachGeneralListeners(): void {
    const content = this.containerEl?.querySelector('#settings-content');
    if (!content) return;

    // Delete buttons
    content.querySelectorAll('.settings-list-delete').forEach(btn => {
      btn.addEventListener('click', async () => {
        const type = btn.getAttribute('data-type');
        const value = btn.getAttribute('data-value');
        if (type && value) {
          await this.deleteItem(type, value);
        }
      });
    });

    // Add buttons
    const addHandlers = [
      { btn: '#add-category-btn', input: '#new-category-input', type: 'category' },
      { btn: '#add-platform-btn', input: '#new-platform-input', type: 'platform' },
      { btn: '#add-format-btn', input: '#new-format-input', type: 'format' },
      { btn: '#add-area-btn', input: '#new-area-input', type: 'area' }
    ];

    addHandlers.forEach(({ btn, input, type }) => {
      const btnEl = content.querySelector(btn);
      const inputEl = content.querySelector(input) as HTMLInputElement;

      btnEl?.addEventListener('click', () => {
        if (inputEl?.value.trim()) {
          this.addItem(type, inputEl.value.trim());
        }
      });

      inputEl?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && inputEl.value.trim()) {
          this.addItem(type, inputEl.value.trim());
        }
      });
    });
  }

  private async addItem(type: string, value: string): Promise<void> {
    if (!this.settings) return;

    let updated = false;
    switch (type) {
      case 'category':
        if (!this.settings.resourceCategories.includes(value)) {
          this.settings.resourceCategories.push(value);
          updated = true;
        }
        break;
      case 'platform':
        this.settings.platforms = this.settings.platforms || [];
        if (!this.settings.platforms.includes(value)) {
          this.settings.platforms.push(value);
          updated = true;
        }
        break;
      case 'format':
        this.settings.contentFormats = this.settings.contentFormats || [];
        if (!this.settings.contentFormats.includes(value)) {
          this.settings.contentFormats.push(value);
          updated = true;
        }
        break;
      case 'area':
        if (!this.settings.areas.includes(value)) {
          this.settings.areas.push(value);
          updated = true;
        }
        break;
    }

    if (updated) {
      await this.saveSettings();
      this.renderContent();
    }
  }

  private async deleteItem(type: string, value: string): Promise<void> {
    if (!this.settings) return;

    switch (type) {
      case 'category':
        this.settings.resourceCategories = this.settings.resourceCategories.filter(c => c !== value);
        break;
      case 'platform':
        this.settings.platforms = (this.settings.platforms || []).filter(p => p !== value);
        break;
      case 'format':
        this.settings.contentFormats = (this.settings.contentFormats || []).filter(f => f !== value);
        break;
      case 'area':
        this.settings.areas = this.settings.areas.filter(a => a !== value);
        break;
    }

    await this.saveSettings();
    this.renderContent();
  }

  private async saveSettings(): Promise<void> {
    if (!this.settings) return;

    try {
      await fetch(`${API_BASE_URL}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.settings)
      });
      this.showNotification('Settings saved');
    } catch (error) {
      console.error('Failed to save settings:', error);
      this.showNotification('Failed to save settings');
    }
  }

  // ============================================
  // KNOWLEDGE SECTION
  // ============================================

  private renderKnowledgeSection(): string {
    return `
      <div class="settings-cards">
        ${this.renderGDriveCard()}
        ${this.renderUniscribeCard()}
        ${this.renderYouTubeCard()}
      </div>
    `;
  }

  private renderGDriveCard(): string {
    if (!this.gdriveConnected) {
      return `
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">folder</span>
            <h3>Google Drive</h3>
            <span class="settings-status disconnected">Not Connected</span>
          </div>
          <p class="settings-card-hint">Connect Google Drive to sync folders to your Knowledge base.</p>
          <a href="/api/gdrive/auth" class="settings-btn primary">Connect Google Drive</a>
        </div>
      `;
    }

    const lastSyncText = this.gdriveLastSync ? `Last sync: ${this.formatDate(this.gdriveLastSync)}` : 'Never synced';

    return `
      <div class="settings-card">
        <div class="settings-card-header">
          <span class="material-symbols-outlined">folder</span>
          <h3>Google Drive</h3>
          <span class="settings-status connected">Connected</span>
        </div>

        <div class="settings-stats">
          <div class="settings-stat">
            <span class="settings-stat-value">${this.selectedFolders.length}</span>
            <span class="settings-stat-label">Folders</span>
          </div>
          <div class="settings-stat">
            <span class="settings-stat-value">${this.gdriveFilesIndexed}</span>
            <span class="settings-stat-label">Files Indexed</span>
          </div>
        </div>

        <div class="settings-folder-list">
          <label>Select folders to sync:</label>
          <div class="folder-checkboxes">
            ${this.availableFolders.length === 0
              ? '<div class="folder-empty">No folders found</div>'
              : this.availableFolders.map(folder => `
                <label class="folder-checkbox-item">
                  <input type="checkbox" class="gdrive-folder-checkbox"
                    value="${folder.id}"
                    data-name="${folder.name}"
                    ${this.selectedFolders.some(f => f.id === folder.id) ? 'checked' : ''}
                  />
                  <span>${folder.path || folder.name}</span>
                </label>
              `).join('')
            }
          </div>
        </div>

        <label class="settings-checkbox">
          <input type="checkbox" id="gdrive-auto-sync" ${this.gdriveAutoSync ? 'checked' : ''} />
          Auto-sync nightly (3 AM UTC)
        </label>

        <div class="settings-actions">
          <button class="settings-btn" id="gdrive-save">Save Configuration</button>
          <button class="settings-btn primary" id="gdrive-sync" ${this.gdriveSyncing ? 'disabled' : ''}>
            ${this.gdriveSyncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>

        <div class="settings-footer" id="gdrive-footer">${lastSyncText}</div>
      </div>
    `;
  }

  private renderUniscribeCard(): string {
    const lastSyncText = this.uniscribeLastSync ? `Last sync: ${this.formatDate(this.uniscribeLastSync)}` : 'Never synced';

    if (!this.uniscribeConfigured) {
      return `
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">mic</span>
            <h3>Uniscribe Transcripts</h3>
            <span class="settings-status disconnected">Not Configured</span>
          </div>
          <p class="settings-card-hint">Connect Uniscribe to index your audio/video transcriptions.</p>
          <div class="settings-input-group">
            <label for="uniscribe-api-key">API Key:</label>
            <input type="password" id="uniscribe-api-key" placeholder="Enter your Uniscribe API key" />
          </div>
          <label class="settings-checkbox">
            <input type="checkbox" id="uniscribe-auto-sync-new" checked />
            Auto-sync nightly (3 AM UTC)
          </label>
          <button class="settings-btn primary" id="uniscribe-connect">Connect Uniscribe</button>
        </div>
      `;
    }

    return `
      <div class="settings-card">
        <div class="settings-card-header">
          <span class="material-symbols-outlined">mic</span>
          <h3>Uniscribe Transcripts</h3>
          <span class="settings-status connected">Connected</span>
        </div>

        <div class="settings-stats">
          <div class="settings-stat">
            <span class="settings-stat-value">${this.uniscribeTranscriptionsIndexed}</span>
            <span class="settings-stat-label">Transcripts</span>
          </div>
          <div class="settings-stat">
            <span class="settings-stat-value">${this.uniscribeApiKeyPreview || '****'}</span>
            <span class="settings-stat-label">API Key</span>
          </div>
        </div>

        <label class="settings-checkbox">
          <input type="checkbox" id="uniscribe-auto-sync" ${this.uniscribeAutoSync ? 'checked' : ''} />
          Auto-sync nightly (3 AM UTC)
        </label>

        <div class="settings-actions">
          <button class="settings-btn danger" id="uniscribe-disconnect">Disconnect</button>
          <button class="settings-btn primary" id="uniscribe-sync" ${this.uniscribeSyncing ? 'disabled' : ''}>
            ${this.uniscribeSyncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>

        <div class="settings-footer" id="uniscribe-footer">${lastSyncText}</div>
      </div>
    `;
  }

  private renderYouTubeCard(): string {
    const lastCheckText = this.youtubeLastCheck ? `Last check: ${this.formatDate(this.youtubeLastCheck)}` : 'Never checked';

    if (!this.youtubeApiKeyConfigured) {
      return `
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">smart_display</span>
            <h3>YouTube Playlists</h3>
            <span class="settings-status disconnected">Not Available</span>
          </div>
          <p class="settings-card-hint">YouTube API key not configured on server. Contact admin to enable.</p>
        </div>
      `;
    }

    if (!this.uniscribeConfigured) {
      return `
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">smart_display</span>
            <h3>YouTube Playlists</h3>
            <span class="settings-status disconnected">Requires Uniscribe</span>
          </div>
          <p class="settings-card-hint">YouTube videos are transcribed via Uniscribe. Configure Uniscribe first.</p>
        </div>
      `;
    }

    return `
      <div class="settings-card">
        <div class="settings-card-header">
          <span class="material-symbols-outlined">smart_display</span>
          <h3>YouTube Playlists</h3>
          <span class="settings-status connected">Ready</span>
        </div>

        <p class="settings-card-hint">Add playlists to monitor → New videos sent to Uniscribe → Transcriptions indexed</p>

        <div class="settings-stats">
          <div class="settings-stat">
            <span class="settings-stat-value">${this.youtubePlaylists.length}</span>
            <span class="settings-stat-label">Playlists</span>
          </div>
          <div class="settings-stat">
            <span class="settings-stat-value">${this.youtubeVideosProcessed}</span>
            <span class="settings-stat-label">Indexed</span>
          </div>
          <div class="settings-stat">
            <span class="settings-stat-value">${this.youtubeVideosPending}</span>
            <span class="settings-stat-label">Pending</span>
          </div>
        </div>

        <div class="settings-playlist-list">
          <label>Monitored playlists:</label>
          <div class="playlist-items">
            ${this.youtubePlaylists.length === 0
              ? '<div class="playlist-empty">No playlists added yet</div>'
              : this.youtubePlaylists.map(pl => `
                <div class="playlist-item">
                  <span>${pl.title}</span>
                  <button class="playlist-remove" data-id="${pl.id}" title="Remove">
                    <span class="material-symbols-outlined">close</span>
                  </button>
                </div>
              `).join('')
            }
          </div>
          <div class="settings-add-row">
            <input type="text" id="youtube-playlist-input" placeholder="Paste playlist URL or ID" />
            <button id="youtube-add-playlist" class="settings-add-btn">Add</button>
          </div>
        </div>

        ${this.youtubeVideosPending > 0 ? `
          <div class="settings-pending">
            <label>Pending transcriptions (${this.youtubeVideosPending}):</label>
            <div class="pending-list">
              ${this.youtubePendingVideos.slice(0, 5).map(v => `
                <div class="pending-item">
                  <span>${v.title}</span>
                  <span class="pending-time">${this.formatDate(v.sentAt)}</span>
                </div>
              `).join('')}
              ${this.youtubeVideosPending > 5 ? `<div class="pending-more">+${this.youtubeVideosPending - 5} more</div>` : ''}
            </div>
          </div>
        ` : ''}

        <label class="settings-checkbox">
          <input type="checkbox" id="youtube-auto-sync" ${this.youtubeAutoSync ? 'checked' : ''} />
          Auto-check nightly (3 AM UTC)
        </label>

        <div class="settings-actions">
          <button class="settings-btn" id="youtube-check" ${this.youtubeSyncing ? 'disabled' : ''}>
            ${this.youtubeSyncing ? 'Checking...' : 'Check for New Videos'}
          </button>
          <button class="settings-btn primary" id="youtube-process" ${this.youtubeProcessing || this.youtubeVideosPending === 0 ? 'disabled' : ''}>
            ${this.youtubeProcessing ? 'Processing...' : 'Process Pending'}
          </button>
        </div>

        <div class="settings-footer" id="youtube-footer">${lastCheckText}</div>
      </div>
    `;
  }

  private attachKnowledgeListeners(): void {
    const content = this.containerEl?.querySelector('#settings-content');
    if (!content) return;

    // GDrive
    content.querySelector('#gdrive-save')?.addEventListener('click', () => this.saveGDriveConfig());
    content.querySelector('#gdrive-sync')?.addEventListener('click', () => this.syncGDrive());

    // Uniscribe
    content.querySelector('#uniscribe-connect')?.addEventListener('click', () => this.connectUniscribe());
    content.querySelector('#uniscribe-disconnect')?.addEventListener('click', () => this.disconnectUniscribe());
    content.querySelector('#uniscribe-sync')?.addEventListener('click', () => this.syncUniscribe());

    // YouTube
    content.querySelector('#youtube-add-playlist')?.addEventListener('click', () => this.addYouTubePlaylist());
    content.querySelector('#youtube-playlist-input')?.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') this.addYouTubePlaylist();
    });
    content.querySelectorAll('.playlist-remove').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        if (id) this.removeYouTubePlaylist(id);
      });
    });
    content.querySelector('#youtube-check')?.addEventListener('click', () => this.checkYouTube());
    content.querySelector('#youtube-process')?.addEventListener('click', () => this.processYouTubePending());
  }

  private async saveGDriveConfig(): Promise<void> {
    const checkboxes = this.containerEl?.querySelectorAll('.gdrive-folder-checkbox:checked');
    this.selectedFolders = Array.from(checkboxes || []).map(cb => {
      const input = cb as HTMLInputElement;
      return { id: input.value, name: input.dataset.name || '' };
    });

    const autoSync = (this.containerEl?.querySelector('#gdrive-auto-sync') as HTMLInputElement)?.checked ?? true;

    try {
      const response = await knowledgeSettings.saveGDriveConfig({ folders: this.selectedFolders, autoSync });
      if (response.success) {
        this.showNotification('GDrive configuration saved');
      }
    } catch (error) {
      this.showNotification('Failed to save GDrive config');
    }
  }

  private async syncGDrive(): Promise<void> {
    if (this.gdriveSyncing) return;
    this.gdriveSyncing = true;
    this.renderContent();

    try {
      const response = await knowledgeSettings.syncGDrive();
      if (response.success && response.data) {
        this.showNotification(`Synced: ${response.data.synced} files`);
        await this.loadKnowledgeData();
      }
    } catch (error) {
      this.showNotification('GDrive sync failed');
    }

    this.gdriveSyncing = false;
    this.renderContent();
  }

  private async connectUniscribe(): Promise<void> {
    const apiKey = (this.containerEl?.querySelector('#uniscribe-api-key') as HTMLInputElement)?.value?.trim();
    const autoSync = (this.containerEl?.querySelector('#uniscribe-auto-sync-new') as HTMLInputElement)?.checked ?? true;

    if (!apiKey) {
      this.showNotification('Please enter API key');
      return;
    }

    try {
      const response = await knowledgeSettings.saveUniscribeConfig({ apiKey, autoSync });
      if (response.success) {
        this.uniscribeConfigured = true;
        await this.loadKnowledgeData();
        this.renderContent();
      } else {
        this.showNotification(response.error || 'Failed to connect');
      }
    } catch (error) {
      this.showNotification('Failed to connect Uniscribe');
    }
  }

  private async disconnectUniscribe(): Promise<void> {
    if (!confirm('Disconnect Uniscribe? Indexed transcriptions will remain.')) return;

    try {
      const response = await knowledgeSettings.deleteUniscribeConfig();
      if (response.success) {
        this.uniscribeConfigured = false;
        this.uniscribeApiKeyPreview = null;
        this.renderContent();
      }
    } catch (error) {
      this.showNotification('Failed to disconnect');
    }
  }

  private async syncUniscribe(): Promise<void> {
    if (this.uniscribeSyncing) return;
    this.uniscribeSyncing = true;
    this.renderContent();

    try {
      const response = await knowledgeSettings.syncUniscribe();
      if (response.success && response.data) {
        this.showNotification(`Synced: ${response.data.synced} transcripts`);
        await this.loadKnowledgeData();
      }
    } catch (error) {
      this.showNotification('Uniscribe sync failed');
    }

    this.uniscribeSyncing = false;
    this.renderContent();
  }

  private async addYouTubePlaylist(): Promise<void> {
    const input = this.containerEl?.querySelector('#youtube-playlist-input') as HTMLInputElement;
    const playlistInput = input?.value?.trim();

    if (!playlistInput) {
      this.showNotification('Enter playlist URL or ID');
      return;
    }

    try {
      const response = await knowledgeSettings.addYouTubePlaylist(playlistInput);
      if (response.success && response.data) {
        this.youtubePlaylists = response.data.config.playlists;
        input.value = '';
        this.showNotification(`Added: ${response.data.playlist.title}`);
        this.renderContent();
      } else {
        this.showNotification(response.error || 'Failed to add playlist');
      }
    } catch (error) {
      this.showNotification('Failed to add playlist');
    }
  }

  private async removeYouTubePlaylist(playlistId: string): Promise<void> {
    const playlist = this.youtubePlaylists.find(p => p.id === playlistId);
    if (!confirm(`Remove "${playlist?.title || playlistId}"?`)) return;

    try {
      const response = await knowledgeSettings.removeYouTubePlaylist(playlistId);
      if (response.success && response.data) {
        this.youtubePlaylists = response.data.config.playlists;
        this.renderContent();
      }
    } catch (error) {
      this.showNotification('Failed to remove playlist');
    }
  }

  private async checkYouTube(): Promise<void> {
    if (this.youtubeSyncing) return;
    this.youtubeSyncing = true;
    this.renderContent();

    try {
      const response = await knowledgeSettings.syncYouTube();
      if (response.success && response.data) {
        this.showNotification(`Found ${response.data.newVideosFound} new videos`);
        await this.loadKnowledgeData();
      }
    } catch (error) {
      this.showNotification('YouTube check failed');
    }

    this.youtubeSyncing = false;
    this.renderContent();
  }

  private async processYouTubePending(): Promise<void> {
    if (this.youtubeProcessing) return;
    this.youtubeProcessing = true;
    this.renderContent();

    try {
      const response = await knowledgeSettings.processYouTubePending();
      if (response.success && response.data) {
        this.showNotification(`Processed ${response.data.processed} videos`);
        await this.loadKnowledgeData();
      }
    } catch (error) {
      this.showNotification('Processing failed');
    }

    this.youtubeProcessing = false;
    this.renderContent();
  }

  // ============================================
  // PATTERNS SECTION
  // ============================================

  private renderPatternsSection(): string {
    return `
      <div class="settings-cards">
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">auto_awesome</span>
            <h3>Pattern Detection</h3>
          </div>
          <p class="settings-card-hint">Configure how patterns are detected and tracked.</p>

          <div class="settings-field">
            <label>Performance Threshold</label>
            <div class="settings-slider">
              <input type="range" id="pattern-threshold" min="5" max="30" value="15" />
              <span id="pattern-threshold-value">15%</span>
            </div>
            <p class="settings-field-hint">Minimum performance to mark as emerging</p>
          </div>

          <div class="settings-field">
            <label>Saturation Video Count</label>
            <div class="settings-slider">
              <input type="range" id="pattern-saturation" min="20" max="200" value="50" step="10" />
              <span id="pattern-saturation-value">50</span>
            </div>
            <p class="settings-field-hint">Videos needed to consider a pattern saturated</p>
          </div>

          <label class="settings-checkbox">
            <input type="checkbox" id="pattern-auto-analyze" checked />
            Auto-analyze on new content
          </label>

          <div class="settings-field">
            <label>Pattern Types to Track</label>
            <div class="settings-checkbox-group">
              <label class="settings-checkbox"><input type="checkbox" checked /> Hooks</label>
              <label class="settings-checkbox"><input type="checkbox" checked /> Script Structure</label>
              <label class="settings-checkbox"><input type="checkbox" checked /> Visual</label>
              <label class="settings-checkbox"><input type="checkbox" checked /> CTA</label>
              <label class="settings-checkbox"><input type="checkbox" checked /> Concepts</label>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private attachPatternsListeners(): void {
    const content = this.containerEl?.querySelector('#settings-content');
    if (!content) return;

    const thresholdSlider = content.querySelector('#pattern-threshold') as HTMLInputElement;
    const thresholdValue = content.querySelector('#pattern-threshold-value');
    thresholdSlider?.addEventListener('input', () => {
      if (thresholdValue) thresholdValue.textContent = `${thresholdSlider.value}%`;
    });

    const saturationSlider = content.querySelector('#pattern-saturation') as HTMLInputElement;
    const saturationValue = content.querySelector('#pattern-saturation-value');
    saturationSlider?.addEventListener('input', () => {
      if (saturationValue) saturationValue.textContent = saturationSlider.value;
    });
  }

  // ============================================
  // OUTREACH SECTION
  // ============================================

  private renderOutreachSection(): string {
    return `
      <div class="settings-cards">
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">smart_toy</span>
            <h3>AI Provider</h3>
          </div>
          <p class="settings-card-hint">Configure AI for generating outreach messages.</p>

          <div class="settings-field">
            <label>Provider</label>
            <select id="outreach-provider" class="settings-select">
              <option value="cloudflare" selected>Cloudflare AI</option>
              <option value="openai">OpenAI</option>
            </select>
          </div>

          <div class="settings-field">
            <label>Model</label>
            <select id="outreach-model" class="settings-select">
              <option value="@cf/meta/llama-3.1-8b-instruct" selected>Llama 3.1 8B</option>
              <option value="@cf/qwen/qwen3-30b-a3b-fp8">Qwen 3 30B</option>
            </select>
          </div>
        </div>

        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">edit_note</span>
            <h3>Default Prompt</h3>
          </div>
          <p class="settings-card-hint">Template for generating outreach messages.</p>

          <textarea id="outreach-prompt" class="settings-textarea" rows="6" placeholder="Enter your default prompt template...">Generate a professional outreach message for {creator_name} about their content on {platform}. Keep it concise and personalized.</textarea>

          <div class="settings-actions">
            <button class="settings-btn" id="outreach-test">Test Prompt</button>
            <button class="settings-btn primary" id="outreach-save-prompt">Save</button>
          </div>
        </div>
      </div>
    `;
  }

  private attachOutreachListeners(): void {
    const content = this.containerEl?.querySelector('#settings-content');
    if (!content) return;

    content.querySelector('#outreach-save-prompt')?.addEventListener('click', () => {
      this.showNotification('Outreach settings saved');
    });

    content.querySelector('#outreach-test')?.addEventListener('click', () => {
      this.showNotification('Test message generated (check console)');
    });
  }

  // ============================================
  // API SECTION
  // ============================================

  private renderApiSection(): string {
    if (!this.ingestDocs) {
      return '<div class="settings-loading">Loading API documentation...</div>';
    }

    return `
      <div class="settings-cards">
        <!-- Stats Card -->
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">analytics</span>
            <h3>Ingest Statistics</h3>
          </div>
          <div class="settings-stats">
            <div class="settings-stat">
              <span class="settings-stat-value">${this.ingestTotal}</span>
              <span class="settings-stat-label">Total Ingested</span>
            </div>
            <div class="settings-stat">
              <span class="settings-stat-value">${this.ingestPrefilled}</span>
              <span class="settings-stat-label">AI Classified</span>
            </div>
            <div class="settings-stat">
              <span class="settings-stat-value">${this.ingestManual}</span>
              <span class="settings-stat-label">Manual</span>
            </div>
          </div>
          <div class="settings-actions">
            <button class="settings-btn" id="api-retrain" ${this.isRetraining ? 'disabled' : ''}>
              ${this.isRetraining ? 'Retraining...' : 'Retrain Classifier'}
            </button>
          </div>
          <div class="settings-footer">${this.ingestLastIngest ? `Last ingest: ${this.formatDate(this.ingestLastIngest)}` : 'Never ingested'}</div>
        </div>

        <!-- Endpoint Card -->
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">api</span>
            <h3>Ingest Endpoint</h3>
          </div>
          <p class="settings-card-hint">Use this endpoint to programmatically add content.</p>

          <div class="settings-endpoint">
            <code>${this.ingestDocs.method} ${this.ingestDocs.endpoint}</code>
            <button class="settings-copy-btn" data-copy="${this.ingestDocs.endpoint}">
              <span class="material-symbols-outlined">content_copy</span>
            </button>
          </div>

          <h4>Headers</h4>
          <pre class="settings-code">${JSON.stringify(this.ingestDocs.headers, null, 2)}</pre>

          <h4>Request Body Schema</h4>
          <pre class="settings-code">${JSON.stringify(this.ingestDocs.body, null, 2)}</pre>
        </div>

        <!-- Examples Card -->
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">code</span>
            <h3>Examples</h3>
          </div>

          <div class="settings-example">
            <h4>Direct Content</h4>
            <pre class="settings-code">${JSON.stringify(this.ingestDocs.examples.directContent, null, 2)}</pre>
            <button class="settings-copy-btn" data-copy='${JSON.stringify(this.ingestDocs.examples.directContent)}'>
              <span class="material-symbols-outlined">content_copy</span>
            </button>
          </div>

          <div class="settings-example">
            <h4>YouTube Transcription</h4>
            <pre class="settings-code">${JSON.stringify(this.ingestDocs.examples.youtubeTranscription, null, 2)}</pre>
            <button class="settings-copy-btn" data-copy='${JSON.stringify(this.ingestDocs.examples.youtubeTranscription)}'>
              <span class="material-symbols-outlined">content_copy</span>
            </button>
          </div>

          <div class="settings-example">
            <h4>Web Extraction</h4>
            <pre class="settings-code">${JSON.stringify(this.ingestDocs.examples.webExtraction, null, 2)}</pre>
            <button class="settings-copy-btn" data-copy='${JSON.stringify(this.ingestDocs.examples.webExtraction)}'>
              <span class="material-symbols-outlined">content_copy</span>
            </button>
          </div>

          <div class="settings-example">
            <h4>Outreach Evaluate</h4>
            <pre class="settings-code">${JSON.stringify(this.ingestDocs.examples.outreachEvaluate, null, 2)}</pre>
            <button class="settings-copy-btn" data-copy='${JSON.stringify(this.ingestDocs.examples.outreachEvaluate)}'>
              <span class="material-symbols-outlined">content_copy</span>
            </button>
          </div>

          <div class="settings-example">
            <h4>Outreach Confirm</h4>
            <pre class="settings-code">${JSON.stringify(this.ingestDocs.examples.outreachConfirm, null, 2)}</pre>
            <button class="settings-copy-btn" data-copy='${JSON.stringify(this.ingestDocs.examples.outreachConfirm)}'>
              <span class="material-symbols-outlined">content_copy</span>
            </button>
          </div>

          <h4>Debug Endpoint</h4>
          <div class="settings-endpoint">
            <code>POST ${this.ingestDocs.debugEndpoint?.url || '/api/ingest/debug'}</code>
            <button class="settings-copy-btn" data-copy="${this.ingestDocs.debugEndpoint?.url || '/api/ingest/debug'}">
              <span class="material-symbols-outlined">content_copy</span>
            </button>
          </div>
          <p class="settings-card-hint">Send requests here to see exactly what was received without processing.</p>
        </div>

        <!-- Source Activity Card -->
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">monitoring</span>
            <h3>Source Activity</h3>
            <button class="settings-card-action" id="clear-source-activity" title="Clear logs">
              <span class="material-symbols-outlined">delete</span>
            </button>
          </div>
          ${this.renderSourceActivity()}
        </div>

        <!-- Outscraper Reviews Card -->
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">rate_review</span>
            <h3>Scrape Reviews</h3>
          </div>
          <p class="settings-card-hint">Scrape competitor reviews for audience research</p>

          <div class="settings-field">
            <label>Platform</label>
            <select id="review-platform" class="settings-select">
              <option value="g2">G2</option>
              <option value="reddit">Reddit</option>
              <option value="producthunt">Product Hunt</option>
              <option value="trustpilot">Trustpilot</option>
              <option value="capterra">Capterra</option>
            </select>
          </div>

          <div class="settings-field">
            <label>Product URL</label>
            <input type="text" id="review-url" class="settings-input" placeholder="https://www.g2.com/products/jasper-ai/reviews">
          </div>

          <div class="settings-field">
            <label>Project Name</label>
            <input type="text" id="review-project" class="settings-input" placeholder="Jasper AI">
          </div>

          <div class="settings-field">
            <label>Limit</label>
            <input type="number" id="review-limit" class="settings-input" value="50" min="1" max="200">
          </div>

          <div class="settings-actions">
            <button class="settings-btn primary" id="btn-scrape-reviews">Scrape Reviews</button>
          </div>

          <div id="review-result" style="margin-top: 12px; color: var(--text-secondary);"></div>
        </div>

        <!-- Request Log Card -->
        <div class="settings-card">
          <div class="settings-card-header">
            <span class="material-symbols-outlined">history</span>
            <h3>Request Log (${this.ingestLogs.length})</h3>
          </div>
          ${this.ingestLogs.length === 0 ? '<p class="settings-card-hint">No requests logged yet</p>' : ''}
          <div class="settings-logs">
            ${this.ingestLogs.slice(0, 50).map((log, index) => {
              const body = log.body as Record<string, unknown> | null;
              const title = body?.title as string || body?.sourceUrl as string || 'Request';
              const source = body?.source as string || 'api';
              return `
              <div class="settings-log-entry ${log.result.success ? 'success' : 'error'}" data-log-index="${index}">
                <div class="log-header">
                  <span class="log-time">${this.formatDate(log.timestamp)}</span>
                  <span class="log-source">${this.escapeHtml(source)}</span>
                  <span class="log-title">${this.escapeHtml(title.slice(0, 40))}</span>
                  <span class="log-status ${log.result.success ? 'success' : 'error'}">${log.result.success ? 'OK' : 'FAIL'}</span>
                  <span class="material-symbols-outlined log-expand">expand_more</span>
                </div>
                ${log.result.error ? `<div class="log-error">${this.escapeHtml(log.result.error)}</div>` : ''}
              </div>
            `;}).join('')}
          </div>
        </div>
      </div>
    `;
  }

  private attachApiListeners(): void {
    const content = this.containerEl?.querySelector('#settings-content');
    if (!content) return;

    // Copy buttons
    content.querySelectorAll('.settings-copy-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const text = btn.getAttribute('data-copy') || '';
        navigator.clipboard.writeText(text);
        this.showNotification('Copied to clipboard');
      });
    });

    // Retrain button
    content.querySelector('#api-retrain')?.addEventListener('click', () => this.retrainClassifier());

    // Scrape reviews button
    content.querySelector('#btn-scrape-reviews')?.addEventListener('click', async () => {
      const platform = (content.querySelector('#review-platform') as HTMLSelectElement)?.value;
      const productUrl = (content.querySelector('#review-url') as HTMLInputElement)?.value?.trim();
      const project = (content.querySelector('#review-project') as HTMLInputElement)?.value?.trim();
      const limit = parseInt((content.querySelector('#review-limit') as HTMLInputElement)?.value || '50', 10);

      if (!productUrl || !project) {
        alert('Please fill Product URL and Project Name');
        return;
      }

      const btn = content.querySelector('#btn-scrape-reviews') as HTMLButtonElement;
      const resultDiv = content.querySelector('#review-result') as HTMLElement;

      btn.disabled = true;
      btn.textContent = 'Scraping...';
      if (resultDiv) resultDiv.textContent = '';

      try {
        const response = await fetch(`${API_BASE_URL}/reviews/scrape`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source: platform, productUrl, project, limit })
        });

        const result = await response.json();
        if (result.success && result.data) {
          if (resultDiv) {
            resultDiv.textContent = `✓ Created ${result.data.created} reviews, skipped ${result.data.skipped} duplicates`;
            resultDiv.style.color = 'var(--primary)';
          }
        } else {
          if (resultDiv) {
            resultDiv.textContent = 'Error: ' + (result.error || 'Failed to scrape reviews');
            resultDiv.style.color = '#ef4444';
          }
        }
      } catch (error) {
        if (resultDiv) {
          resultDiv.textContent = 'Failed to scrape reviews';
          resultDiv.style.color = '#ef4444';
        }
      } finally {
        btn.disabled = false;
        btn.textContent = 'Scrape Reviews';
      }
    });

    // Clear source activity button
    content.querySelector('#clear-source-activity')?.addEventListener('click', async () => {
      if (confirm('Clear all source activity logs?')) {
        try {
          await fetch(`${API_BASE_URL}/settings/source-activity`, { method: 'DELETE' });
          this.sourceActivity = null;
          this.renderContent();
          this.showNotification('Source activity cleared');
        } catch (error) {
          console.error('Failed to clear source activity:', error);
        }
      }
    });

    // Log entry click to view details
    content.querySelectorAll('.settings-log-entry').forEach(entry => {
      entry.addEventListener('click', () => {
        const index = parseInt(entry.getAttribute('data-log-index') || '0', 10);
        const log = this.ingestLogs[index];
        if (log) {
          this.showLogDetail(log);
        }
      });
    });
  }

  private showLogDetail(log: IngestLogEntry): void {
    // Create modal
    const modal = document.createElement('div');
    modal.className = 'log-detail-modal';
    modal.innerHTML = `
      <div class="log-detail-overlay"></div>
      <div class="log-detail-content">
        <div class="log-detail-header">
          <h3>Request Details</h3>
          <span class="log-detail-status ${log.result.success ? 'success' : 'error'}">${log.result.success ? 'SUCCESS' : 'FAILED'}</span>
          <button class="log-detail-close">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <div class="log-detail-body">
          <div class="log-detail-section">
            <h4>Timestamp</h4>
            <p>${this.formatDate(log.timestamp)}</p>
          </div>
          ${log.result.error ? `
          <div class="log-detail-section error">
            <h4>Error</h4>
            <p>${this.escapeHtml(log.result.error)}</p>
          </div>
          ` : ''}
          ${log.result.id ? `
          <div class="log-detail-section">
            <h4>Created ID</h4>
            <code>${this.escapeHtml(log.result.id)}</code>
          </div>
          ` : ''}
          <div class="log-detail-section">
            <h4>Request Body</h4>
            <pre>${JSON.stringify(log.body, null, 2)}</pre>
          </div>
          <div class="log-detail-section">
            <h4>Headers</h4>
            <pre>${JSON.stringify(log.headers, null, 2)}</pre>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Close handlers
    const closeModal = () => modal.remove();
    modal.querySelector('.log-detail-overlay')?.addEventListener('click', closeModal);
    modal.querySelector('.log-detail-close')?.addEventListener('click', closeModal);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeModal();
    }, { once: true });
  }

  private async retrainClassifier(): Promise<void> {
    if (this.isRetraining) return;
    this.isRetraining = true;
    this.renderContent();

    try {
      const response = await fetch(`${API_BASE_URL}/ingest/retrain`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        this.showNotification(`Retrained with ${data.data?.examplesUsed || 0} examples`);
      } else {
        this.showNotification('Retrain failed: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      this.showNotification('Retrain failed');
    }

    this.isRetraining = false;
    this.renderContent();
  }

  // ============================================
  // UTILITIES
  // ============================================

  private renderSourceActivity(): string {
    if (!this.sourceActivity) {
      return '<p class="settings-card-hint">No activity recorded yet</p>';
    }

    const { summary, last24hBySource, recentLogs } = this.sourceActivity;
    const sources = Object.keys(summary);

    if (sources.length === 0) {
      return '<p class="settings-card-hint">No activity recorded yet</p>';
    }

    // Sort sources by last activity
    sources.sort((a, b) => (summary[b].lastActivity || 0) - (summary[a].lastActivity || 0));

    // Calculate max for bar scaling
    const maxCount = Math.max(...Object.values(last24hBySource), 1);

    let html = '<div class="source-activity">';

    // Summary bars
    html += '<div class="source-activity-summary">';
    html += '<div class="source-activity-header">Last 24 hours</div>';

    for (const source of sources) {
      const count24h = last24hBySource[source] || 0;
      const stats = summary[source];
      const barWidth = Math.max(5, (count24h / maxCount) * 100);
      const lastActive = this.formatRelativeTime(stats.lastActivity);
      const types = Object.keys(stats.byType).join(', ');

      html += `
        <div class="source-activity-row">
          <div class="source-name">${this.escapeHtml(source)}</div>
          <div class="source-bar-container">
            <div class="source-bar ${count24h > 0 ? 'active' : ''}" style="width: ${barWidth}%"></div>
          </div>
          <div class="source-stats">
            <span class="source-count">${count24h}</span>
            <span class="source-time">${lastActive}</span>
          </div>
        </div>
      `;
    }
    html += '</div>';

    // Recent logs
    if (recentLogs.length > 0) {
      html += '<div class="source-activity-logs">';
      html += '<div class="source-activity-header">Recent Activity</div>';

      for (const log of recentLogs.slice(0, 15)) {
        const statusClass = log.success ? 'success' : 'error';
        const statusIcon = log.success ? 'check_circle' : 'error';
        const time = this.formatRelativeTime(log.timestamp);

        html += `
          <div class="source-log-entry ${statusClass}">
            <span class="material-symbols-outlined source-log-icon">${statusIcon}</span>
            <span class="source-log-source">${this.escapeHtml(log.source)}</span>
            <span class="source-log-type">${log.type}</span>
            ${log.title ? `<span class="source-log-title">${this.escapeHtml(log.title.slice(0, 30))}</span>` : ''}
            <span class="source-log-time">${time}</span>
          </div>
        `;
      }
      html += '</div>';
    }

    html += '</div>';
    return html;
  }

  private formatRelativeTime(timestamp: number): string {
    const now = Date.now();
    const diffMs = now - timestamp;
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;

    return new Date(timestamp).toLocaleDateString();
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  private formatDate(timestamp: number): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  private showNotification(message: string): void {
    const notification = document.createElement('div');
    notification.className = 'settings-notification';
    notification.textContent = message;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 2000);
  }
}
